<template>
  <content-padding :color="color" rounded class="panel-group">
    <template v-if="tabItems && tabItems.length">
      <ck-tab
        v-model="currentTab"
        :items="tabItems"
        @tab-clicked="onButtonTabClick"
      >
        <slot name="header" />
      </ck-tab>
    </template>
    <template v-else>
      <flex v-if="title || !!$slots.header" justify-content="space-between">
        <h4>{{ title }}</h4>
        <slot name="header" />
        <span v-if="!!$slots.action" class="action">
          <slot name="action" />
        </span>
      </flex>
    </template>
    <ck-skeleton-loader v-if="isLoading && loaderConfig">
      <template v-slot:skeleton>
        <skeleton v-if="loaderConfig.title" width="85px" height="16px" />
        <skeleton v-if="loaderConfig.subtitle" width="105px" height="8px" />
        <skeleton
          v-if="loaderConfig.chart"
          :style="`height: ${loaderConfigChartSize}; position: relative;`"
        >
          <ck-icon
            name="ico-placeholder-graph-260px"
            width="260px"
            height="100px"
            fill="#E6E9F3"
            class="chart"
          />
        </skeleton>
        <skeleton
          v-if="loaderConfig.progressbar"
          height="6px"
          class="progress"
        />
      </template>
    </ck-skeleton-loader>
    <template v-else>
      <slot v-if="!panelPlaceholder" :tab="currentTab" />
      <slot v-if="!panelPlaceholder" name="extra" />
      <div v-if="!!$slots.empty && !panelPlaceholder" class="empty">
        <slot name="empty" />
      </div>
      <panel-placeholder v-if="panelPlaceholder" :data="panelPlaceholder" />
      <slot v-if="panelPlaceholder" name="panelplaceholder" />
      <div v-if="!!$slots.message && !panelPlaceholder" class="message">
        <slot name="message" />
      </div>
      <template v-if="panelButton && !showHistory && !panelPlaceholder">
        <hr />
        <panel-button
          :data="panelButton[0]"
          @click.native="setShowHistory"
          @touch.native="setShowHistory"
        />
      </template>
      <slot v-if="showHistory && !panelPlaceholder" name="history" />
      <template v-if="panelButton && showHistory && !panelPlaceholder">
        <hr />
        <panel-button
          :data="panelButton[1]"
          @click.native="setHideHistory"
          @touch.native="setHideHistory"
        />
      </template>
    </template>
  </content-padding>
</template>

<script>
import PanelButton from '@/components/shared-layout/PanelButton'
import PanelPlaceholder from '@/components/shared-layout/PanelPlaceholder'
// import EmptySlot from '@/components/shared-layout/EmptySlot'
import CkSkeletonLoader from '@/components/kit/CkSkeletonLoader'
import CkTab from '@/components/kit/CkTab'

export default {
  components: {
    PanelButton,
    // EmptySlot,
    PanelPlaceholder,
    CkSkeletonLoader,
    CkTab
  },
  props: {
    color: {
      type: String,
      default: 'white'
    },
    title: String,
    tabItems: Array,
    activeTab: String,
    isLoading: Boolean,
    loaderConfig: Object, // { title: true, subtitle: true, chart: 'small' / 'medium' / 'large', progressbar: true }
    panelPlaceholder: Object, // $panelPlaceholder()
    panelButton: [Object, Array] // $panelButton()
  },
  data() {
    return {
      currentTab: this.activeTab || '1',
      showHistory: false
    }
  },
  computed: {
    loaderConfigChartSize() {
      let size = 0
      if (this.loaderConfig) {
        if (this.loaderConfig.chart === 'large') {
          size = '219px'
        } else if (this.loaderConfig.chart === 'medium') {
          size = '209px'
        } else if (this.loaderConfig.chart === 'small') {
          size = '135px'
        }
      }
      return size
    }
  },
  watch: {
    activeTab() {
      if (
        this.tabItems &&
        this.tabItems.length &&
        this.activeTab !== this.currentTab
      ) {
        this.onButtonTabClick(this.activeTab || this.currentTab)
      }
    }
  },
  methods: {
    setShowHistory() {
      this.showHistory = true
    },
    setHideHistory() {
      this.showHistory = false
    },
    onButtonTabClick(tabId) {
      if (tabId) {
        this.currentTab = tabId
      }
      this.$emit('tab-changed', this.tabItems[this.currentTab - 1])
    }
  }
}
</script>

<style lang="less" scoped>
@import (reference) '~@/assets/less/main.less';

.panel-group {
  ::v-deep {
    .tab-group {
      margin-bottom: @space-xxs !important;
    }

    .skeleton-loader-group {
      .lh-display(flex);
      .lh-flex-direction(column);
      overflow: hidden;

      > * {
        &:first-child {
          margin-bottom: 6px;
        }

        &.progress {
          margin: 33px 0 20px;
        }
      }
    }

    .toggle-group {
      padding-top: 0 !important;
    }

    hr {
      margin-left: -@space-s;
      margin-right: -@space-s;
    }

    .empty {
      .empty-slot;
    }

    .message {
      margin: 0 -@space-s -19px -@space-s;
      width: calc(100% + @space-s + @space-s);
      border-radius: 0 0 @border-radius-s @border-radius-s;
    }
  }

  .action {
    .lh-flex(0 0 20px);
    .lh-display(flex);
    .lh-align-items(center);
    .lh-justify-content(center);
    width: 20px;
    height: 20px;
    background-color: @grey-ultralight;
    border-radius: 50%;

    ::v-deep > .ico {
      vertical-align: top;
    }
  }
}
</style>
